import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, X, Loader2, RefreshCw, Layers, Calendar } from 'lucide-react'
import { examSessionsApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'
import toast from 'react-hot-toast'

// ── Academic year utils ────────────────────────────────────────────────────────

const ACADEMIC_YEAR_START_MONTH = 3 // April (0-indexed)

const formatAcademicYear = (value) => {
  if (!value) return ''
  const s = String(value).trim().replace(/[–—−]/g, '-').replace(/\s+/g, '').replace(/\//g, '-')
  const m = /^(\d{4})-(\d{2}|\d{4})$/.exec(s)
  if (!m) return String(value).trim()
  const startYear = Number(m[1])
  let endYear = Number(m[2])
  if (m[2].length === 2) {
    const base = Math.floor(startYear / 100) * 100
    endYear = base + endYear
    if (endYear < startYear) endYear += 100
  }
  if (endYear !== startYear + 1) return String(value).trim()
  return `${startYear}-${endYear}`
}

const isAcademicYear = (value) => {
  const s = String(value || '').trim().replace(/[–—−]/g, '-').replace(/\s+/g, '').replace(/\//g, '-')
  const m = /^(\d{4})-(\d{2}|\d{4})$/.exec(s)
  if (!m) return false
  const startYear = Number(m[1])
  let endYear = Number(m[2])
  if (m[2].length === 2) {
    const base = Math.floor(startYear / 100) * 100
    endYear = base + endYear
    if (endYear < startYear) endYear += 100
  }
  return endYear === startYear + 1
}

const getCurrentAcademicYear = () => {
  const now = new Date()
  const year = now.getMonth() >= ACADEMIC_YEAR_START_MONTH ? now.getFullYear() : now.getFullYear() - 1
  return `${year}-${year + 1}`
}

const getAcademicYearOptions = ({ pastCount = 1, futureCount = 2 } = {}) => {
  const now = new Date()
  const currentYear = now.getMonth() >= ACADEMIC_YEAR_START_MONTH ? now.getFullYear() : now.getFullYear() - 1
  const options = []
  for (let offset = -pastCount; offset <= futureCount; offset++) {
    const startYear = currentYear + offset
    options.push(`${startYear}-${startYear + 1}`)
  }
  return options
}

// ── Constants ──────────────────────────────────────────────────────────────────

const EXAM_SESSION_STATUSES = ['draft', 'scheduled', 'ongoing', 'completed', 'published']
const STATUS_FILTERS = ['all', ...EXAM_SESSION_STATUSES]

const STATUS_STYLES = {
  draft:     { bg: 'bg-slate-100',   text: 'text-slate-600',   dot: '#64748b' },
  scheduled: { bg: 'bg-blue-50',     text: 'text-blue-700',    dot: '#2563eb' },
  ongoing:   { bg: 'bg-teal-50',     text: 'text-teal-700',    dot: '#0f766e' },
  completed: { bg: 'bg-purple-50',   text: 'text-purple-700',  dot: '#7c3aed' },
  published: { bg: 'bg-emerald-50',  text: 'text-emerald-700', dot: '#16a34a' },
  cancelled: { bg: 'bg-red-50',      text: 'text-red-600',     dot: '#ef4444' },
}

const DEFAULT_GRADING_SCALE = [
  { label: 'A+', minPercentage: 90 },
  { label: 'A',  minPercentage: 80 },
  { label: 'B',  minPercentage: 70 },
  { label: 'C',  minPercentage: 60 },
  { label: 'D',  minPercentage: 50 },
  { label: 'F',  minPercentage: 0  },
]

const makeEmptyForm = () => ({
  name:          '',
  academicYear:  getCurrentAcademicYear(),
  startDate:     '',
  endDate:       '',
  description:   '',
  gradingScale:  DEFAULT_GRADING_SCALE.map((g) => ({ ...g })),
})

const formatDate = (value) => {
  if (!value) return ''
  try {
    return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
  } catch { return value }
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function ExamSessionsPage() {
  const [allSessions, setAllSessions]       = useState([])
  const [loading, setLoading]               = useState(true)
  const [saving, setSaving]                 = useState(false)
  const [statusFilter, setStatusFilter]     = useState('all')
  const currentAcademicYear                 = useMemo(() => getCurrentAcademicYear(), [])
  const [yearFilter, setYearFilter]         = useState(currentAcademicYear)
  const [modalOpen, setModalOpen]           = useState(false)
  const [editing, setEditing]               = useState(null)
  const [form, setForm]                     = useState(makeEmptyForm)
  const [formErrors, setFormErrors]         = useState({})
  const [deleteId, setDeleteId]             = useState(null)
  const [deleting, setDeleting]             = useState(false)
  const academicYearOptions                 = useMemo(() => getAcademicYearOptions({ pastCount: 1, futureCount: 2 }), [])

  const fetchSessions = async () => {
    try {
      setLoading(true)
      const data = await examSessionsApi.getAll()
      setAllSessions(Array.isArray(data) ? data : data?.examSessions ?? data?.sessions ?? [])
    } catch {
      toast.error('Failed to load exam sessions')
      setAllSessions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchSessions() }, [])

  const yearOptions = useMemo(() => {
    const values = new Set(allSessions.map((s) => formatAcademicYear(s.academicYear)).filter(Boolean))
    values.add(currentAcademicYear)
    return ['all', ...Array.from(values).sort((a, b) => b.localeCompare(a))]
  }, [allSessions, currentAcademicYear])

  const filteredSessions = useMemo(() => allSessions.filter((s) => {
    const matchStatus = statusFilter === 'all' || (s.status || '').toLowerCase() === statusFilter
    const matchYear   = yearFilter   === 'all' || formatAcademicYear(s.academicYear) === yearFilter
    return matchStatus && matchYear
  }), [allSessions, statusFilter, yearFilter])

  // ── Helpers ────────────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditing(null)
    setForm(makeEmptyForm())
    setFormErrors({})
    setModalOpen(true)
  }

  const openEdit = (session) => {
    setEditing(session._id || session.id)
    setForm({
      name:         session.name || '',
      academicYear: formatAcademicYear(session.academicYear) || currentAcademicYear,
      startDate:    session.startDate ? String(session.startDate).slice(0, 10) : '',
      endDate:      session.endDate   ? String(session.endDate).slice(0, 10)   : '',
      description:  session.description || '',
      gradingScale: Array.isArray(session.gradingScale) && session.gradingScale.length > 0
        ? session.gradingScale.map((g) => ({ label: g.label || '', minPercentage: Number(g.minPercentage ?? 0) }))
        : DEFAULT_GRADING_SCALE.map((g) => ({ ...g })),
    })
    setFormErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const errors = {}
    const normalizedYear = formatAcademicYear(form.academicYear)
    if (!form.name.trim())            errors.name         = 'Session name is required'
    if (!form.academicYear.trim())    errors.academicYear = 'Academic year is required'
    else if (!isAcademicYear(normalizedYear)) errors.academicYear = 'Use YYYY-YYYY format (e.g. 2025-2026)'
    if (!form.startDate)              errors.startDate    = 'Start date is required'
    if (!form.endDate)                errors.endDate      = 'End date is required'
    if (form.startDate && form.endDate && new Date(form.endDate) < new Date(form.startDate))
      errors.endDate = 'End date must be after start date'
    form.gradingScale.forEach((g, i) => {
      if (!g.label.trim())                                         errors[`grade-${i}`]     = 'Required'
      const pct = Number(g.minPercentage)
      if (Number.isNaN(pct) || pct < 0 || pct > 100)              errors[`grade-min-${i}`] = '0–100'
    })
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    const normalizedYear = formatAcademicYear(form.academicYear)
    const payload = {
      name:         form.name.trim(),
      academicYear: normalizedYear,
      startDate:    form.startDate,
      endDate:      form.endDate,
      description:  form.description.trim(),
      gradingScale: form.gradingScale.map((g) => ({ label: g.label.trim(), minPercentage: Number(g.minPercentage) })),
    }
    try {
      setSaving(true)
      if (editing) {
        const updated = await examSessionsApi.update(editing, payload)
        setAllSessions((prev) => prev.map((s) => (s._id || s.id) === editing ? { ...s, ...updated } : s))
        toast.success('Session updated')
      } else {
        const created = await examSessionsApi.create(payload)
        setAllSessions((prev) => [...prev, created])
        toast.success('Session created')
      }
      setModalOpen(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save session')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      await examSessionsApi.remove(deleteId)
      setAllSessions((prev) => prev.filter((s) => (s._id || s.id) !== deleteId))
      toast.success('Session deleted')
      setDeleteId(null)
    } catch {
      toast.error('Failed to delete session')
    } finally {
      setDeleting(false)
    }
  }

  const updateGrade = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      gradingScale: prev.gradingScale.map((g, i) =>
        i === index ? { ...g, [field]: field === 'minPercentage' ? Number(value || 0) : value } : g
      ),
    }))

  const editingSession   = editing ? allSessions.find((s) => (s._id || s.id) === editing) : null
  const autoStatus       = editingSession?.status || 'draft'
  const autoStatusLabel  = autoStatus.charAt(0).toUpperCase() + autoStatus.slice(1)
  const autoStatusColor  = STATUS_STYLES[autoStatus]?.dot || '#64748b'

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <PageHeader
        title="Exam Sessions"
        subtitle="Group exams into academic terms. Grading scales defined here apply to session results."
        chips={[
          loading ? 'Loading...' : `${allSessions.length} sessions`,
          currentAcademicYear,
        ]}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={fetchSessions} className="btn-secondary p-2" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={openAdd} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Session
            </button>
          </div>
        }
      />

      {/* ── Filters ── */}
      <div className="card p-4 space-y-3">
        {/* Status */}
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f
            return (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`text-xs font-semibold px-4 py-1.5 rounded-full border transition-colors capitalize ${
                  active
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {f}
              </button>
            )
          })}
        </div>
        {/* Academic year */}
        {yearOptions.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {yearOptions.map((y) => {
              const active = yearFilter === y
              return (
                <button
                  key={y}
                  onClick={() => setYearFilter(y)}
                  className={`text-xs font-semibold px-4 py-1.5 rounded-full border transition-colors ${
                    active
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {y}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Sessions grid ── */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="card p-5 animate-pulse space-y-3">
              <div className="h-5 bg-gray-100 rounded w-2/3" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
              <div className="h-8 bg-gray-100 rounded" />
            </div>
          ))
        ) : filteredSessions.length === 0 ? (
          <div className="col-span-full card p-10 flex flex-col items-center gap-3 text-center">
            <Layers className="w-10 h-10 text-gray-200" />
            <p className="text-sm text-gray-400">No exam sessions match the current filter.</p>
            {yearFilter !== 'all' && (
              <button onClick={() => setYearFilter('all')} className="btn-secondary text-xs">
                Show all years
              </button>
            )}
          </div>
        ) : filteredSessions.map((session) => {
          const statusKey = (session.status || 'draft').toLowerCase()
          const colors    = STATUS_STYLES[statusKey] || STATUS_STYLES.draft
          return (
            <div key={session._id || session.id} className="card p-5 space-y-3">
              {/* Top row */}
              <div className="flex items-start justify-between gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Layers className="w-5 h-5 text-purple-600" />
                </div>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${colors.bg} ${colors.text}`}>
                  {statusKey}
                </span>
              </div>
              {/* Name + year */}
              <div>
                <h3 className="font-bold text-gray-900">{session.name}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{formatAcademicYear(session.academicYear)}</p>
              </div>
              {/* Date range */}
              {(session.startDate || session.endDate) && (
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  {formatDate(session.startDate)}
                  {session.endDate ? ` → ${formatDate(session.endDate)}` : ''}
                </div>
              )}
              {session.description && (
                <p className="text-xs text-gray-500 line-clamp-2">{session.description}</p>
              )}
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs font-semibold bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full">
                  {(session.gradingScale || []).length} grade bands
                </span>
                {session.examsCount != null && (
                  <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full">
                    {session.examsCount} exam{session.examsCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => openEdit(session)}
                  className="btn-secondary flex-1 text-xs py-1.5"
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeleteId(session._id || session.id)}
                  className="flex-1 text-xs py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Create/Edit Modal ── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="font-bold text-gray-900">
                {editing ? 'Edit Exam Session' : 'Create Exam Session'}
              </h3>
              <button onClick={() => setModalOpen(false)}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4">

              {/* ── Session Details ── */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-4">
                <p className="text-sm font-semibold text-gray-700">Session Details</p>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Session Name *</label>
                  <input
                    className={`input-field w-full ${formErrors.name ? 'border-red-400' : ''}`}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Mid Term"
                  />
                  {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Academic Year *</label>
                  <input
                    className={`input-field w-full ${formErrors.academicYear ? 'border-red-400' : ''}`}
                    value={form.academicYear}
                    onChange={(e) => setForm({ ...form, academicYear: e.target.value })}
                    onBlur={() => setForm((prev) => ({ ...prev, academicYear: formatAcademicYear(prev.academicYear) }))}
                    placeholder={currentAcademicYear}
                  />
                  {formErrors.academicYear && <p className="text-xs text-red-500 mt-1">{formErrors.academicYear}</p>}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {academicYearOptions.map((y) => {
                      const active = form.academicYear === y
                      return (
                        <button key={y} type="button" onClick={() => setForm({ ...form, academicYear: y })}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border-[1.5px] transition-colors ${active ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                          {y}
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Format: YYYY-YYYY. April to March is one academic year.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Start Date *</label>
                    <input type="date" className={`input-field w-full ${formErrors.startDate ? 'border-red-400' : ''}`}
                      value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                    {formErrors.startDate && <p className="text-xs text-red-500 mt-1">{formErrors.startDate}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">End Date *</label>
                    <input type="date" className={`input-field w-full ${formErrors.endDate ? 'border-red-400' : ''}`}
                      value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} min={form.startDate || undefined} />
                    {formErrors.endDate && <p className="text-xs text-red-500 mt-1">{formErrors.endDate}</p>}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
                  <textarea className="input-field w-full resize-none" rows={3}
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Mid term examination cycle" />
                </div>
              </div>

              {/* ── Automatic Status (edit only) ── */}
              {editing && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                  <p className="text-sm font-semibold text-gray-700">Automatic Status</p>
                  <div className="rounded-xl border px-4 py-3 space-y-2"
                    style={{ borderColor: `${autoStatusColor}40`, backgroundColor: `${autoStatusColor}12` }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: autoStatusColor }} />
                      <span className="text-sm font-semibold" style={{ color: autoStatusColor }}>{autoStatusLabel}</span>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Status is calculated automatically from linked exams, the session dates, and result publication.
                    </p>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Draft until you add an exam. Scheduled before the session starts. Ongoing during the session window. Completed after the end date. Published after admin publishes the linked class results.
                    </p>
                    {editingSession && (
                      <p className="text-xs font-semibold text-gray-600">
                        {editingSession.examsCount || 0} exams linked
                        {editingSession.activeExamsCount != null
                          ? ` · ${editingSession.publishedExamsCount || 0} of ${editingSession.activeExamsCount} active exams published`
                          : ''}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Grading Scale ── */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">Grading Scale</p>
                  <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full">Highest to lowest</span>
                </div>
                {form.gradingScale.map((grade, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Grade {index + 1}</label>
                      <input className={`input-field w-full ${formErrors[`grade-${index}`] ? 'border-red-400' : ''}`}
                        value={grade.label} onChange={(e) => updateGrade(index, 'label', e.target.value)} placeholder="A+" />
                      {formErrors[`grade-${index}`] && <p className="text-xs text-red-500 mt-0.5">{formErrors[`grade-${index}`]}</p>}
                    </div>
                    <div className="w-24">
                      <label className="block text-xs font-semibold text-gray-500 mb-1">Min %</label>
                      <input type="number" min="0" max="100"
                        className={`input-field w-full ${formErrors[`grade-min-${index}`] ? 'border-red-400' : ''}`}
                        value={grade.minPercentage} onChange={(e) => updateGrade(index, 'minPercentage', e.target.value.replace(/[^0-9]/g, ''))} placeholder="90" />
                      {formErrors[`grade-min-${index}`] && <p className="text-xs text-red-500 mt-0.5">{formErrors[`grade-min-${index}`]}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-5 py-4 flex gap-3 justify-end border-t border-gray-100 flex-shrink-0">
              <button onClick={() => setModalOpen(false)} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Update Session' : 'Create Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Delete Session?</h3>
            <p className="text-sm text-gray-500 mb-5">All associated exams may be affected.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary flex-1" disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
